/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
    SecurityHelper,
    Button,
    StyledModal,
    ModalActions,
    ButtonGroup,
} from 'sarsaparilla';
import * as tripPlannerActions from '../actions/tripPlanner';

const propTypes = {
    enableOverviewModal: PropTypes.bool,
    preferences: PropTypes.object,
    dispatch: PropTypes.func,
};

const defaultProps = {
    enableOverviewModal: false,
    preferences: {
        hide_intro: false,
        hide_booking_intro: false,
    },
};

export class TripPlannerOverviewModal extends React.Component {
    constructor(props) {
        super(props);

        this.refresh();

        this.state = {
            isOverviewModalOpen: false,
            step: 1,
            doNotShowAgain: false,
        };

        this.onModalClose = this.onModalClose.bind(this);
        this.onModalNext = this.onModalNext.bind(this);
        this.handleOnChange = this.handleOnChange.bind(this);
        this.refresh = this.refresh.bind(this);
    }

    componentDidMount() {
        if (this.props.enableOverviewModal && !this.props.preferences.hide_intro) {
            this.setState({ isOverviewModalOpen: true });
        }
    }

    UNSAFE_componentWillReceiveProps(nextProps) {
        if (JSON.stringify(nextProps) !== JSON.stringify(this.props)) {
            let shouldDisplayOverviewModal = false;

            if (nextProps.enableOverviewModal && !nextProps.preferences.hide_intro) {
                shouldDisplayOverviewModal = true;
            }

            this.setState({ isOverviewModalOpen: shouldDisplayOverviewModal });
        }
    }

    onModalClose() {
        this.setState({ isOverviewModalOpen: false });

        // Disable overview modal since we have already shown it, else it will
        // show when page is refreshed. This is set true from the Starting page
        // when the user clicks on the "Start Planning" button.
        this.props.dispatch(tripPlannerActions.setEnableOverviewModal(false));

        // Check if we need to update user preference to supress future modal
        if (this.state.doNotShowAgain) {
            this.props.dispatch(tripPlannerActions.setHideIntro(true));

            if (SecurityHelper.isLoggedIn()) {
                this.props.dispatch(tripPlannerActions.saveUserPreferences());
            } else {
                sessionStorage.setItem('doNotShowAgainOverviewModalUserSetting', true);
            }
        }
    }

    onModalNext() {
        this.setState((prevState) => {
            return { step: prevState.step + 1 };
        });
    }

    getStepView() {
        if (this.state.step === 1) {
            return (
                <>
                    <div className="trip-planner-overview-modal-view">
                        <div className="rec-modal-content-wrap">
                            <p>
                                Start by discovering Accommodations and Things to do along
                                your route.
                            </p>
                            <picture>
                                <img
                                    src={`${process.env.CDN}/img/trip-planner/modal_img_1.jpg`}
                                    alt="Trip Planning Things to Do Example"
                                />
                            </picture>
                        </div>
                    </div>
                    <ModalActions>
                        <ButtonGroup>
                            <Button
                                title="Next: Add to your Itinerary"
                                aria-label="Next: Add to your Itinerary"
                                onClick={this.onModalNext}
                            >
                                Next: Add to your Itinerary
                            </Button>
                        </ButtonGroup>
                    </ModalActions>
                </>
            );
        }
        if (this.state.step === 2) {
            return (
                <>
                    <div className="trip-planner-overview-modal-view">
                        <div className="rec-modal-content-wrap">
                            <p>
                                Click on inventory icon to add that item to your
                                itinerary. Click <strong>Book Now</strong> when you are
                                ready to checkout.
                            </p>
                            <picture>
                                <img
                                    src={`${process.env.CDN}/img/trip-planner/modal_img_2.jpg`}
                                    alt="Trip Planning Book Now Example"
                                />
                            </picture>
                        </div>
                    </div>
                    <ModalActions>
                        <Button
                            className="rec-button-primary"
                            title="Next: Reserve your Campsite and Activities"
                            aria-label="Next: Reserve your Campsite and Activities"
                            onClick={this.onModalNext}
                        >
                            Next: Reserve your Campsite and Activities
                        </Button>
                    </ModalActions>
                </>
            );
        }

        return (
            <>
                <div className="trip-planner-overview-modal-view">
                    <div className="rec-modal-content-wrap">
                        <p>
                            Create your reservations and complete your itinerary. You can
                            access your itinerary from your account at any time.
                        </p>
                        <picture>
                            <img
                                src={`${process.env.CDN}/img/trip-planner/modal_img_3.jpg`}
                                alt="Trip Planning Itinerary Example"
                            />
                        </picture>
                    </div>
                </div>
                <ModalActions>
                    <div className="trip-planner-do-not-show-again-input">
                        <label htmlFor="doNotShowAgain" className="rec-label-checkbox">
                            <input
                                className="rec-input-hide"
                                type="checkbox"
                                id="doNotShowAgain"
                                name="doNotShowAgain"
                                checked={!!this.state.doNotShowAgain}
                                onChange={this.handleOnChange}
                            />
                            <span className="rec-input-checkbox" />
                            Do not show again
                        </label>
                    </div>
                    <ButtonGroup>
                        <Button
                            title="Get Started"
                            aria-label="Get Started"
                            onClick={this.onModalClose}
                        >
                            Get Started
                        </Button>
                    </ButtonGroup>
                </ModalActions>
            </>
        );
    }

    refresh() {
        if (SecurityHelper.isLoggedIn()) {
            this.props.dispatch(tripPlannerActions.fetchUserPreferences());
        } else {
            const doNotShowAgainOverviewModalUserSetting = sessionStorage.getItem(
                'doNotShowAgainOverviewModalUserSetting'
            );
            if (doNotShowAgainOverviewModalUserSetting) {
                this.props.dispatch(
                    tripPlannerActions.setHideIntro(
                        doNotShowAgainOverviewModalUserSetting
                    )
                );
            }
        }
    }

    handleOnChange(e) {
        this.setState({ doNotShowAgain: e.target.checked });
    }

    render() {
        return (
            <StyledModal
                isOpen={this.state.isOverviewModalOpen}
                onRequestClose={this.onModalClose}
                role="complementary"
                ariaHideApp={false}
                contentLabel="Trip Planner Overview"
            >
                {this.getStepView()}
            </StyledModal>
        );
    }
}

TripPlannerOverviewModal.propTypes = propTypes;
TripPlannerOverviewModal.defaultProps = defaultProps;

function select(state) {
    return {
        enableOverviewModal: state.tripPlanner.enableOverviewModal,
        preferences: state.tripPlanner.preferences,
    };
}

export default connect(select)(TripPlannerOverviewModal);
